import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SECTION_ORDER, type PhotoSection } from '@room-verify/shared';
import { ApiError, uploadPhotoWithRetry, type Inspection, type UploadPhase } from '../api';
import {
  Button,
  IconArrowRight,
  IconBath,
  IconBed,
  IconCamera,
  IconCheck,
  IconCheckCircle,
  IconDoor,
  IconKitchen,
  IconRefresh,
  ProgressRing,
} from '../components';
import {
  CameraError,
  captureFrame,
  cameraDiagnostics,
  detachStream,
  logCameraDiagnostics,
  snapshotVideo,
  startCameraSession,
  stopCamera,
  type Capture,
} from '../camera';
import { canAutoRestart, isFrameReady, shouldRestartOnForeground } from '../camera-lifecycle';
import { errorMessage, type Copy } from '../i18n';

/**
 * Camera pipeline state — deliberately separate from whether a shot is being reviewed.
 *
 * Conflating the two is what broke this screen on iOS: reviewing a photo used to unmount
 * the video element, which suspends the stream on iOS and never resumes it.
 */
type CameraState = 'idle' | 'starting' | 'live' | 'recovering' | 'error';

/** How long a camera must keep working before it earns a fresh restart budget. */
const STABLE_AFTER_MS = 10_000;

const slotKey = (section: PhotoSection, sequence: number) => `${section}:${sequence}`;

const SECTION_ICON: Record<PhotoSection, (props: { size?: number }) => JSX.Element> = {
  ROOM_NUMBER: IconDoor,
  BATHROOM: IconBath,
  KITCHEN: IconKitchen,
  ROOM: IconBed,
};

export function CaptureScreen({
  copy,
  token,
  inspection,
  onAllCaptured,
  onAuthExpired,
  onToast,
}: {
  copy: Copy;
  token: string;
  inspection: Inspection;
  onAllCaptured: (confirmed: Set<string>) => void;
  onAuthExpired: () => void;
  onToast: (text: string, tone?: 'default' | 'success' | 'danger') => void;
}) {
  const sectionLabel = useMemo<Record<PhotoSection, string>>(
    () => ({
      ROOM_NUMBER: copy.sectionRoomNumber,
      BATHROOM: copy.sectionBathroom,
      KITCHEN: copy.sectionKitchen,
      ROOM: copy.sectionRoom,
    }),
    [copy],
  );

  const sectionHint = useMemo<Record<PhotoSection, string>>(
    () => ({
      ROOM_NUMBER: copy.sectionRoomNumberHint,
      BATHROOM: copy.sectionBathroomHint,
      KITCHEN: copy.sectionKitchenHint,
      ROOM: copy.sectionRoomHint,
    }),
    [copy],
  );

  /** Slots the server has confirmed. Seeded from the server, never from the device. */
  const [confirmed, setConfirmed] = useState<Set<string>>(
    () => new Set(inspection.photos.map((photo) => slotKey(photo.section, photo.sequence))),
  );

  const [cameraState, setCameraState] = useState<CameraState>('idle');
  const [cameraFailure, setCameraFailure] = useState<CameraError | null>(null);
  const [capture, setCapture] = useState<Capture | null>(null);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const viewfinderRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  /** True once the supervisor has asked for the camera and still needs it. */
  const wantsCamera = useRef(false);
  /** Serialises startup so overlapping restarts cannot fight over the element. */
  const startLatch = useRef(false);
  const autoRestarts = useRef(0);
  const stableTimer = useRef<number | null>(null);
  const detachTrackListeners = useRef<(() => void) | null>(null);

  // Synchronous latches. `disabled={busy}` is not in effect until the next render, and
  // three taps on a phone land inside that window.
  const uploadLatch = useRef(false);
  const captureLatch = useRef(false);

  const sections = useMemo(
    () => SECTION_ORDER.filter((section) => inspection.requirements[section] > 0),
    [inspection.requirements],
  );

  const totalRequired = useMemo(
    () => sections.reduce((total, section) => total + inspection.requirements[section], 0),
    [sections, inspection.requirements],
  );

  /** The next unconfirmed slot, in capture order. */
  const currentSlot = useMemo(() => {
    for (const section of sections) {
      for (let sequence = 1; sequence <= inspection.requirements[section]; sequence += 1) {
        if (!confirmed.has(slotKey(section, sequence))) return { section, sequence };
      }
    }
    return null;
  }, [sections, inspection.requirements, confirmed]);

  const allDone = currentSlot === null;

  /* ------------------------------------------------------------- camera -- */

  const releaseCapture = useCallback((value: Capture | null) => {
    if (value) URL.revokeObjectURL(value.previewUrl);
  }, []);

  const clearStableTimer = useCallback(() => {
    if (stableTimer.current !== null) {
      window.clearTimeout(stableTimer.current);
      stableTimer.current = null;
    }
  }, []);

  /** Full teardown: listeners, element, tracks. Safe to call repeatedly. */
  const teardownCamera = useCallback(() => {
    clearStableTimer();
    detachTrackListeners.current?.();
    detachTrackListeners.current = null;
    detachStream(videoRef.current);
    stopCamera(streamRef.current);
    streamRef.current = null;
  }, [clearStableTimer]);

  /**
   * Watches the live track for the ways iOS takes a camera away underneath us.
   *
   * `ended` is terminal. `mute` means capture is suspended (a call, the lock screen, the
   * app going background) — and `unmute` is not a promise of frames, so it is verified
   * rather than trusted.
   */
  const wireTrackListeners = useCallback((stream: MediaStream) => {
    detachTrackListeners.current?.();
    const track = stream.getVideoTracks()[0];
    if (!track) return;

    const onEnded = () => {
      logCameraDiagnostics(
        'track_ended',
        cameraDiagnostics(videoRef.current, streamRef.current, { outcome: 'ended' }),
      );
      void restartCameraRef.current?.('track-ended');
    };

    const onMute = () => {
      if (!wantsCamera.current) return;
      setCameraState((previous) => (previous === 'live' ? 'recovering' : previous));
    };

    const onUnmute = () => {
      if (!wantsCamera.current) return;
      // Give the pipeline a beat, then insist on real frames before claiming live.
      window.setTimeout(() => {
        if (!wantsCamera.current) return;
        const snapshot = snapshotVideo(videoRef.current, streamRef.current);
        if (isFrameReady(snapshot)) setCameraState('live');
        else void restartCameraRef.current?.('unmute-without-frames');
      }, 400);
    };

    track.addEventListener('ended', onEnded);
    track.addEventListener('mute', onMute);
    track.addEventListener('unmute', onUnmute);

    detachTrackListeners.current = () => {
      track.removeEventListener('ended', onEnded);
      track.removeEventListener('mute', onMute);
      track.removeEventListener('unmute', onUnmute);
    };
  }, []);

  /**
   * Starts, or restarts, the camera and only reports live once frames are confirmed.
   *
   * `manual` marks an explicit "Restart Camera" tap, which resets the automatic budget —
   * the cap exists to stop a broken device looping, not to stop a person retrying.
   */
  const startCamera = useCallback(
    async (trigger: string, { manual = false }: { manual?: boolean } = {}) => {
      const video = videoRef.current;
      if (!video) return;
      if (startLatch.current) return;

      if (manual) {
        autoRestarts.current = 0;
      } else if (!canAutoRestart(autoRestarts.current)) {
        setCameraFailure(new CameraError('NO_FRAMES'));
        setCameraState('error');
        return;
      }

      startLatch.current = true;
      wantsCamera.current = true;
      if (!manual && trigger !== 'open') autoRestarts.current += 1;

      setCameraFailure(null);
      setCameraState((previous) => (previous === 'live' ? 'recovering' : 'starting'));

      teardownCamera();

      try {
        const session = await startCameraSession(video);
        streamRef.current = session.stream;
        wireTrackListeners(session.stream);
        setCameraState('live');

        // A camera that survives ten seconds has proven itself; give it a fresh budget.
        clearStableTimer();
        stableTimer.current = window.setTimeout(() => {
          autoRestarts.current = 0;
        }, STABLE_AFTER_MS);

        // Bring the viewfinder into view rather than leaving it half below the fold.
        requestAnimationFrame(() => {
          viewfinderRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
        });
      } catch (error) {
        const failure = error instanceof CameraError ? error : new CameraError('UNKNOWN');
        logCameraDiagnostics(
          'session_failed',
          cameraDiagnostics(video, null, { outcome: `${trigger}:${failure.reason}` }),
        );
        teardownCamera();
        setCameraFailure(failure);
        setCameraState('error');
      } finally {
        startLatch.current = false;
      }
    },
    [teardownCamera, wireTrackListeners, clearStableTimer],
  );

  // Track listeners are wired before `startCamera` is defined, so they reach it by ref.
  const restartCameraRef = useRef<((trigger: string) => Promise<void>) | null>(null);
  useEffect(() => {
    restartCameraRef.current = (trigger: string) => startCamera(trigger);
  }, [startCamera]);

  const openCamera = useCallback(() => {
    autoRestarts.current = 0;
    void startCamera('open');
  }, [startCamera]);

  const restartCamera = useCallback(() => {
    void startCamera('manual', { manual: true });
  }, [startCamera]);

  /**
   * iOS suspends capture whenever the app leaves the foreground — backgrounded, locked,
   * or interrupted by a call. What comes back is inconsistent, so returning to the
   * foreground re-checks the pipeline against reality and rebuilds it if needed.
   */
  useEffect(() => {
    const onForeground = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (!wantsCamera.current) return;
      // Let iOS finish restoring before judging the pipeline.
      window.setTimeout(() => {
        if (!wantsCamera.current) return;
        const snapshot = snapshotVideo(videoRef.current, streamRef.current);
        if (shouldRestartOnForeground(snapshot)) {
          logCameraDiagnostics(
            'foreground_restart',
            cameraDiagnostics(videoRef.current, streamRef.current, { outcome: 'foreground' }),
          );
          void startCamera('foreground');
        }
      }, 350);
    };

    // The page being frozen or put in the bfcache must not leave the camera light on.
    const onPageHide = () => teardownCamera();

    document.addEventListener('visibilitychange', onForeground);
    window.addEventListener('pageshow', onForeground);
    window.addEventListener('focus', onForeground);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onForeground);
      window.removeEventListener('pageshow', onForeground);
      window.removeEventListener('focus', onForeground);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [startCamera, teardownCamera]);

  // Release the camera when leaving the screen. A live camera light left on after the
  // supervisor walks away is both a battery cost and a trust problem.
  useEffect(
    () => () => {
      wantsCamera.current = false;
      teardownCamera();
    },
    [teardownCamera],
  );

  // Once every photo is captured the camera is no longer needed.
  useEffect(() => {
    if (allDone) {
      wantsCamera.current = false;
      teardownCamera();
      setCameraState('idle');
    }
  }, [allDone, teardownCamera]);

  useEffect(() => () => releaseCapture(capture), [capture, releaseCapture]);

  /* ------------------------------------------------------------ actions -- */

  const takePhoto = useCallback(async () => {
    if (captureLatch.current) return;
    const video = videoRef.current;
    if (!video) return;

    captureLatch.current = true;
    try {
      const frame = await captureFrame(video);
      setCapture(frame);
      setUploadError(null);
      setUploadPhase(null);
    } catch (error) {
      // A capture that finds no frames means the pipeline died between render and tap.
      const failure = error instanceof CameraError ? error : new CameraError('UNKNOWN');
      if (failure.reason === 'NO_FRAMES') void startCamera('capture-without-frames');
      else {
        setCameraFailure(failure);
        setCameraState('error');
      }
    } finally {
      captureLatch.current = false;
    }
  }, [startCamera]);

  const retake = useCallback(() => {
    releaseCapture(capture);
    setCapture(null);
    setUploadError(null);
    setUploadPhase(null);
  }, [capture, releaseCapture]);

  const usePhoto = useCallback(async () => {
    if (uploadLatch.current) return;
    if (!capture || !currentSlot) return;

    uploadLatch.current = true;
    setUploadError(null);
    try {
      const photo = await uploadPhotoWithRetry(
        token,
        inspection.id,
        {
          section: currentSlot.section,
          sequence: currentSlot.sequence,
          capturedAt: capture.capturedAt,
        },
        capture.blob,
        (phase) => setUploadPhase(phase),
      );

      setConfirmed((previous) => {
        const next = new Set(previous);
        next.add(slotKey(photo.section, photo.sequence));
        return next;
      });

      releaseCapture(capture);
      setCapture(null);
      setUploadPhase(null);
      onToast(copy.uploaded, 'success');
    } catch (caught) {
      const error = caught instanceof ApiError ? caught : new ApiError('NETWORK', 0);

      if (error.isAuthExpiry) {
        // The capture is deliberately kept: after re-login the supervisor taps
        // "Use Photo" again rather than re-shooting the room.
        setUploadPhase(null);
        setUploadError(copy.sessionKeptSafe);
        onAuthExpired();
        return;
      }

      if (error.code === 'INSPECTION_LOCKED') {
        setUploadError(copy.errLocked);
      } else if (error.retryable) {
        setUploadError(copy.errNetworkPhoto);
      } else {
        setUploadError(errorMessage(copy, error.code));
      }
      setUploadPhase(null);
    } finally {
      uploadLatch.current = false;
    }
  }, [capture, currentSlot, token, inspection.id, copy, onToast, onAuthExpired, releaseCapture]);

  /* -------------------------------------------------------------- render -- */

  const doneCount = confirmed.size;
  const reviewing = capture !== null;

  const uploadStatusText =
    uploadPhase === 'uploading'
      ? copy.uploading
      : uploadPhase === 'retrying'
        ? copy.retrying
        : null;

  const failureTitle = (failure: CameraError): string => {
    switch (failure.reason) {
      case 'DENIED':
        return copy.cameraDeniedTitle;
      case 'INSECURE':
        return copy.cameraInsecureTitle;
      case 'NOT_FOUND':
        return copy.cameraMissingTitle;
      default:
        return copy.cameraUnavailableTitle;
    }
  };

  const failureText = (failure: CameraError): string => {
    switch (failure.reason) {
      case 'DENIED':
        return copy.cameraDeniedText;
      case 'INSECURE':
        return copy.cameraInsecureText;
      case 'NOT_FOUND':
        return copy.cameraMissingText;
      default:
        return copy.cameraUnavailableText;
    }
  };

  return (
    <div className="screen capture-screen">
      <header className="capture-head card">
        <ProgressRing value={doneCount} total={totalRequired} />
        <div className="capture-head-text">
          <p className="capture-room">
            {copy.roomNumber} <strong className="num">{inspection.roomNumber}</strong>
          </p>
          <p className="capture-progress num">{copy.photosProgress(doneCount, totalRequired)}</p>
        </div>
      </header>

      <ul className="section-list">
        {sections.map((section) => {
          const needed = inspection.requirements[section];
          const done = Array.from({ length: needed }).filter((_, index) =>
            confirmed.has(slotKey(section, index + 1)),
          ).length;
          const isCurrent = currentSlot?.section === section;
          const Icon = SECTION_ICON[section];

          return (
            <li
              key={section}
              className={`section-row${isCurrent ? ' is-current' : ''}${
                done === needed ? ' is-done' : ''
              }`}
            >
              <span className="section-icon" aria-hidden="true">
                {done === needed ? <IconCheck size={16} /> : <Icon size={16} />}
              </span>
              <span className="section-name">{sectionLabel[section]}</span>
              <span className="section-count num">
                {done} / {needed}
              </span>
              <span className="section-bar" aria-hidden="true">
                <span
                  className="section-bar-fill"
                  style={{ transform: `scaleX(${needed > 0 ? done / needed : 0})` }}
                />
              </span>
            </li>
          );
        })}
      </ul>

      {allDone ? (
        <div className="capture-done card card-pad">
          <span className="capture-done-icon" aria-hidden="true">
            <IconCheckCircle size={26} />
          </span>
          <p className="capture-done-title">{copy.allPhotosDone}</p>
          <p className="capture-done-text num">{copy.photosProgress(doneCount, totalRequired)}</p>
        </div>
      ) : (
        <section className="capture-stage" aria-labelledby="slot-title">
          <div className="capture-slot-head">
            <h2 id="slot-title" className="capture-slot-title">
              {sectionLabel[currentSlot.section]} · {copy.photoSlot(currentSlot.sequence)}
            </h2>
            <p className="capture-slot-hint">{sectionHint[currentSlot.section]}</p>
          </div>

          <div className="viewfinder" ref={viewfinderRef}>
            {/*
              The video element is mounted for the whole life of this screen and is never
              given `display: none`. Hiding it — which the previous build did while a photo
              was being reviewed — suspends the stream on iOS and it does not come back,
              so every shot after the first showed a black rectangle. Overlays sit on top
              of it instead, so the pipeline keeps running underneath.
            */}
            <video
              ref={videoRef}
              className="viewfinder-video"
              playsInline
              muted
              autoPlay
              disablePictureInPicture
              aria-hidden="true"
            />

            {reviewing && capture && (
              <img className="viewfinder-preview" src={capture.previewUrl} alt="" />
            )}

            {!reviewing && cameraState === 'idle' && (
              <div className="viewfinder-overlay">
                <span className="viewfinder-icon" aria-hidden="true">
                  <IconCamera size={30} />
                </span>
                <Button variant="accent" onClick={openCamera} icon={<IconCamera size={18} />}>
                  {copy.openCamera}
                </Button>
              </div>
            )}

            {!reviewing && (cameraState === 'starting' || cameraState === 'recovering') && (
              <div className="viewfinder-overlay">
                <span className="spinner viewfinder-spinner" />
                <p className="viewfinder-text">
                  {cameraState === 'recovering' ? copy.cameraRecovering : copy.cameraStarting}
                </p>
              </div>
            )}

            {!reviewing && cameraState === 'error' && cameraFailure && (
              <div className="viewfinder-overlay viewfinder-error">
                <p className="viewfinder-error-title">{failureTitle(cameraFailure)}</p>
                <p className="viewfinder-text">{failureText(cameraFailure)}</p>
                <Button variant="outline" onClick={restartCamera} icon={<IconRefresh size={16} />}>
                  {copy.restartCamera}
                </Button>
              </div>
            )}
          </div>

          {uploadStatusText && (
            <p className="upload-status" role="status" aria-live="polite">
              <span className="spinner" />
              {uploadStatusText}
            </p>
          )}

          {uploadError && (
            <div className="notice notice-danger" role="alert">
              {uploadError}
            </div>
          )}

          <div className="sticky-actions">
            {!reviewing && cameraState === 'live' && (
              <Button
                variant="accent"
                size="lg"
                block
                onClick={takePhoto}
                icon={<IconCamera size={20} />}
              >
                {copy.capture}
              </Button>
            )}

            {!reviewing && cameraState === 'error' && (
              <Button
                variant="accent"
                size="lg"
                block
                onClick={restartCamera}
                icon={<IconRefresh size={18} />}
              >
                {copy.restartCamera}
              </Button>
            )}

            {reviewing && (
              <div className="action-pair">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={retake}
                  disabled={uploadPhase !== null}
                  icon={<IconRefresh size={18} />}
                >
                  {copy.retake}
                </Button>
                <Button
                  variant="accent"
                  size="lg"
                  onClick={usePhoto}
                  loading={uploadPhase === 'uploading' || uploadPhase === 'retrying'}
                  icon={<IconCheck size={18} />}
                >
                  {copy.usePhoto}
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {allDone && (
        <div className="sticky-actions">
          <Button
            variant="accent"
            size="lg"
            block
            onClick={() => onAllCaptured(confirmed)}
            icon={<IconArrowRight size={18} />}
          >
            {copy.reviewItems}
          </Button>
        </div>
      )}
    </div>
  );
}
